/**
 * PerfProbe — update() 섹션별 처리시간 계측 + 프레임 지표 수집 조립기.
 *
 * section()/begin()/end()로 구간 ms를 재고(performance.now), tick()으로 PerfSource에서 전역
 * 지표(fps·heap·객체수)를 읽어 perfStats에 공급한다. enabled=false면 계측 비용 0(즉시 fn 실행).
 * now는 주입 가능 — 테스트 결정론 유지.
 */
import type { PerfStats } from "./perfStats.js";
import type { PerfSource, PerfFrameSample } from "./types.js";

export interface PerfProbeOptions {
  /** 시간 소스 (기본 performance.now). 테스트에서 주입. */
  now?: () => number;
}

export interface PerfProbe {
  enabled: boolean;
  /** fn을 실행하며 처리시간을 section 라벨로 기록(enabled일 때만). 반환값 그대로 패스스루. */
  section<T>(name: string, fn: () => T): T;
  /**
   * 프레임 전역 지표 수집 → perfStats.frame. enabled일 때 수집한 raw 샘플을 반환(없으면 null).
   * 호출측(perfTool)이 그 raw 샘플을 렉/임계 모니터에 재공급해 source.sample() 이중 호출을 피한다.
   */
  tick(): PerfFrameSample | null;
  /** 표시용 라인. */
  format(): string[];
}

const defaultNow = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : 0;

export function createPerfProbe(
  stats: PerfStats,
  source: PerfSource,
  opts: PerfProbeOptions = {},
): PerfProbe {
  const now = opts.now ?? defaultNow;
  const probe: PerfProbe = {
    enabled: false,
    section<T>(name: string, fn: () => T): T {
      if (!probe.enabled) return fn();
      const t0 = now();
      try {
        return fn();
      } finally {
        stats.mark(name, now() - t0);
      }
    },
    tick(): PerfFrameSample | null {
      if (!probe.enabled) return null;
      const sample = source.sample();
      stats.frame(sample);
      return sample;
    },
    format: () => stats.format(),
  };
  return probe;
}
